// @ts-check

import { get, getAll } from "@/utilities/querySelector";

/**
 * It's not a true web component as it requires specific HTML content to actually function
 * but it's a neat way of packing up the functionality IMO
 */
export class TabGroupHTMLElement extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.tabsWrap = get( '[data-tabs]', this );
        this.panelsWrap = get( '[data-panels]', this );
        this.tabs = getAll( '[data-tab]', this );
        this.panels = getAll( '[data-panel]', this );
        this.breakpoint = window.matchMedia( '(min-width: 1024px)' );

        this.tabs.forEach( tab => {
            tab.addEventListener( 'click', this.onTabClick.bind( this ) );
        } );

        this.assignMode();
        this.breakpoint.addEventListener( 'change', this.assignMode.bind( this ) );
        this.setAttribute( 'data-initialized', '' );
    }

    /**
     * @param {MouseEvent} e
     *
     * @return void
     */
    onTabClick( e ) {
        const tab = e.currentTarget;

        if ( !( tab instanceof HTMLElement ) ) return;

        this.tabs.forEach( tab => tab.removeAttribute( 'data-active' ) );
        tab.setAttribute( 'data-active', '' );

        const panel = this.getTabsPanel( tab );

        if ( !( panel instanceof HTMLElement ) ) return;

        const activepanel = get( '[data-panel][data-active]', this );

        if ( activepanel === panel ) return;

        e.preventDefault();

        const anim = this.deactivate( activepanel );
        anim.addEventListener( 'finish', async () => {
            let anim = this.activate( panel );
            await anim.finished;
            if ( this.getMode() === 'mobile' ) {
                panel?.scrollIntoView();
            }
        } );
    }

    assignMode() {
        if ( this.breakpoint.matches ) {
            this.setDesktopMode();
        } else {
            this.setMobileMode();
        }
    }

    getMode() {
        return this.breakpoint.matches ? 'desktop' : 'mobile';
    }

    setMobileMode() {
        this.tabs.forEach( tab => {
            const panel = this.getTabsPanel( tab );
            tab.after( panel );
        } );
    }

    setDesktopMode() {
        this.tabs.forEach( tab => {
            const panel = this.getTabsPanel( tab );
            tab.after( panel );
            this.panelsWrap.appendChild( panel );
        } );
    }

    /**
     *
     * @param {HTMLElement} tab
     *
     * @returns {HTMLElement|undefined}
     */
    getTabsPanel( tab ) {
        const id = tab.dataset.tab;
        return get( `[data-panel="${id}"]`, this );
    }

    /**
     *
     * To be used externally for whatever reason
     *
     * @param {string} id The name associate with the panel
     */
    goTo( id ) {
        const tab = get( `[data-tab="${id}"]`, this );
        const panel = get( `[data-panel="${id}"]`, this );

        if ( !( panel instanceof HTMLElement ) ) return;

        this.tabs.forEach( tab => tab.removeAttribute( 'data-active' ) );
        tab.setAttribute( 'data-active', '' );

        const activepanel = get( '[data-panel][data-active]', this );

        if ( activepanel === panel ) return panel?.scrollIntoView();

        const anim = this.deactivate( activepanel );
        anim.addEventListener( 'finish', async () => {
            let anim = this.activate( panel );
            await anim.finished;
            panel?.scrollIntoView();
        } );
    }

    /**
     * @param {HTMLElement} panel
     *
     * @return {Animation}
     */
    deactivate( panel ) {
        const animation = panel.animate(
            {
                opacity: [1, 0],
                translate: ['0 0', '1rem 0']
            }, { duration: 200, easing: 'ease' }
        );
        animation.addEventListener( 'finish', () => {
            panel.removeAttribute( 'data-active' );
        } );
        return animation;
    }

    /**
     * @param {HTMLElement} panel
     *
     * @return {Animation}
     */
    activate( panel ) {
        panel.setAttribute( 'data-active', '' );
        const animation = panel.animate( {
            opacity: [0, 1],
            translate: ['1rem 0', '0 0']
        }, { duration: 200, easing: 'ease' } );
        return animation;
    }
}

if ( !customElements.get( 'tab-group' ) ) {
    customElements.define( 'tab-group', TabGroupHTMLElement );
}